/** @jsx jsx */
import React, { type ReactNode } from 'react';
import { css, jsx } from '@compiled/react';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { usePathParam } from '@atlassian/react-resource-router';
import { customQueueRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customQueueRoute.tsx';
import { customPracticeQueueRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customPracticeQueueRoute.tsx';
import { customPracticeQueueBoardRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customPracticeQueueBoardRoute.tsx';
import { customQueueBoardRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customQueueBoardRoute.tsx';
import { customQueueCalendarRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customQueueCalendarRoute.tsx';
import { customPracticeQueueCalendarRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customPracticeQueueCalendarRoute.tsx';
import { customIssueRoute } from '@atlassian/jira-router-routes-servicedesk-queue-routes/src/customIssueRoute.tsx';

const SEGMENT_KEY = 'queues-agent-view-top-layout-nav3';

export const useStylingPatch = () => {
	const route = useCurrentRoute();
	const [issueKey] = usePathParam('issueKey');

	if (!fg('jsm_views_inside_queues_-_main_flag'))
		return { shouldUseStylingPatch: false, isNav3QueueIssueViewRoute: false };

	const isNav3QueueRouteOld =
		!getWillShowNav4() &&
		(route.name === customQueueRoute.name ||
			(route.name === customPracticeQueueRoute.name && !issueKey));

	const isNav3QueueRouteNew =
		!getWillShowNav4() &&
		(route.name === customQueueRoute.name ||
			route.name === customPracticeQueueRoute.name ||
			// Only the Nav3 Queue content and Nav3 Category Queue content are wrapped with QueueNav3StylingPatchContainer.
			// The routes where we should doing this wrapping are queue route, category queue route and issue view route
			// The reason why we are also need to wrap the Queue content on issue view route is because we want to avoid re-rendering.
			// ----------------------------------------------------------------------------------------------------------
			// Inside the QueueNav3StylingPatchContainer (below), we will avoid putting the height on issue view route (because of this scroll problem).
			// This will not trigger the re-rendering because the DOM structure isn’t changed.
			route.name === customIssueRoute.name);

	// customIssueRoute is issue view route for custom queue
	// while customPracticeQueueRoute contains route for both practice queue agent view and practice queue issue view, we need to check issueKey to determine if it's issue view route
	const isNav3QueueIssueViewRoute =
		!getWillShowNav4() &&
		(route.name === customIssueRoute.name ||
			(route.name === customPracticeQueueRoute.name && !!issueKey));

	const isNav3QueueRoute = fg('jsm_fix_queue_issue_view_navigate_back_state')
		? isNav3QueueRouteNew
		: isNav3QueueRouteOld;

	const isNav3BoardRoute =
		!getWillShowNav4() &&
		(route.name === customQueueBoardRoute.name ||
			route.name === customPracticeQueueBoardRoute.name);

	const isNav3CalendarRoute =
		!getWillShowNav4() &&
		(route.name === customQueueCalendarRoute.name ||
			route.name === customPracticeQueueCalendarRoute.name);

	return {
		shouldUseStylingPatch: isNav3QueueRoute || isNav3BoardRoute || isNav3CalendarRoute,
		isNav3QueueIssueViewRoute,
	};
};

export const QueueNav3StylingPatchContainer = ({ children }: { children: ReactNode }) => {
	const { shouldUseStylingPatch, isNav3QueueIssueViewRoute } = useStylingPatch();

	if (shouldUseStylingPatch || !fg('jsm_fix_queue_issue_view_navigate_back_state'))
		return (
			<div
				data-vc={
					fg('further_improve_vc90_performence_of_queues_page')
						? `${SEGMENT_KEY}${__SERVER__ ? '-ssr' : ''}`
						: undefined
				}
				data-ssr-placeholder={
					__SERVER__ && fg('further_improve_vc90_performence_of_queues_page')
						? SEGMENT_KEY
						: undefined
				}
				data-ssr-placeholder-replace={
					__SERVER__ || !fg('further_improve_vc90_performence_of_queues_page')
						? undefined
						: SEGMENT_KEY
				}
				css={!isNav3QueueIssueViewRoute && nav3PaddingStyles}
				data-testid="horizontal-nav-queue.controllers.styling-patch.queue-nav-3-styling-patch-container"
			>
				{children}
			</div>
		);

	return children;
};

const nav3PaddingStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	paddingTop: '137px',
	boxSizing: 'border-box',
	display: 'flex',
	flexDirection: 'column',
	height: '100%',
});
